/**
 * The only things this app keeps in the browser: choices that belong to the device or to one tab.
 * Nothing about a person or a session is kept here — the server holds that, and the app reads it on
 * every load. A new key is added below, on purpose; storageGuard.test.ts fails on storage used anywhere else.
 */

/** A choice kept in the browser: the name it has always been kept under, and whether it outlives a sign-out. */
interface StoredChoice {
  readonly name: string;
  readonly outlivesSignOut: boolean;
}

/** Kept on this device. */
const DEVICE_KEYS = {
  /** The language this device shows, before anyone signs in and after they leave. The language detector keeps it too. */
  language: { name: 'i18nextLng', outlivesSignOut: true },
  /** This device's push registration. */
  pushToken: { name: 'FireBase', outlivesSignOut: true },
  /** When the person here was last asked to allow alerts: the next person is asked for themselves. */
  pushAsked: { name: 'ctl-push-asked', outlivesSignOut: false },
} as const satisfies Readonly<Record<string, StoredChoice>>;

/** How the person here laid out a screen; the next person starts from the screen's own layout. */
const LAYOUT_KEYS = {
  /** The split of a resizable two-panel screen that names no split of its own. */
  panelSplit: { name: 'resizable-panel-size', outlivesSignOut: false },
  /** The split between the scanned sheet and the readings on OCR data input. */
  ocrInputSplit: { name: 'ocr-input-panel-split', outlivesSignOut: false },
  /** The split between the scanned sheet and the logbook on OCR logbook setup. */
  ocrLogbookSplit: { name: 'ocr-panel-split-width', outlivesSignOut: false },
} as const satisfies Readonly<Record<string, StoredChoice>>;

/** Kept by one tab for as long as it is open; a sign-out leaves it, so a tab can return where it was. */
const TAB_KEYS = {
  /** Where the tab was when its session ended, to return there after signing in again. */
  returnPath: { name: 'dp-session-return-path', outlivesSignOut: true },
  /** When the tab last reloaded to fetch a missing page bundle, so it never reloads in a loop. */
  chunkReloadAt: { name: 'app:lazy-chunk-reload-ts', outlivesSignOut: true },
} as const satisfies Readonly<Record<string, StoredChoice>>;

export type DeviceKey = keyof typeof DEVICE_KEYS;
export type LayoutKey = keyof typeof LAYOUT_KEYS;
export type TabKey = keyof typeof TAB_KEYS;

/** What outlives a sign-out in this browser's lasting storage; everything else there is the last person's. */
const KEPT_THROUGH_SIGN_OUT: readonly string[] = [...Object.values(DEVICE_KEYS), ...Object.values(LAYOUT_KEYS)]
  .filter((choice) => choice.outlivesSignOut)
  .map((choice) => choice.name);

type Area = 'localStorage' | 'sessionStorage';

// A browser can refuse storage outright (private modes, blocked site data): every access is guarded.
const storageOf = (area: Area): Storage | null => {
  try {
    return window[area];
  } catch {
    return null;
  }
};

const read = (storage: Storage | null, name: string): string | null => {
  try {
    return storage?.getItem(name) ?? null;
  } catch {
    return null;
  }
};

const write = (storage: Storage | null, name: string, value: string): void => {
  try {
    storage?.setItem(name, value);
  } catch {
    // Full or blocked: the choice is simply not kept.
  }
};

const remove = (storage: Storage | null, name: string): void => {
  try {
    storage?.removeItem(name);
  } catch {
    // Blocked: there is nothing kept to remove.
  }
};

const keyed = <K extends string>(area: Area, choices: Readonly<Record<K, StoredChoice>>) => ({
  read: (key: K): string | null => read(storageOf(area), choices[key].name),
  write: (key: K, value: string): void => write(storageOf(area), choices[key].name, value),
  remove: (key: K): void => remove(storageOf(area), choices[key].name),
});

export const deviceStorage = keyed('localStorage', DEVICE_KEYS);
export const layoutStorage = keyed('localStorage', LAYOUT_KEYS);
export const tabStorage = keyed('sessionStorage', TAB_KEYS);

/** Every name this module may keep in the browser's lasting storage — what an end-to-end check allows there. */
export const LASTING_STORAGE_NAMES: readonly string[] = [...Object.values(DEVICE_KEYS), ...Object.values(LAYOUT_KEYS)].map(
  (choice) => choice.name,
);

/** The name a device choice is kept under, for a library that keeps it itself — the language detector does. */
export const storedNameOf = (key: DeviceKey): string => DEVICE_KEYS[key].name;

/** Clears what was kept while someone was signed in; only what outlives a sign-out stays. */
export const clearForNextPerson = (): void => {
  const storage = storageOf('localStorage');
  if (storage === null) return;
  const kept = KEPT_THROUGH_SIGN_OUT.map((name) => [name, read(storage, name)] as const);
  try {
    storage.clear();
  } catch {
    return;
  }
  kept.forEach(([name, value]) => {
    if (value !== null) write(storage, name, value);
  });
};
